import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  hubService,
  type CreateMessageBundleDto,
  type MessageBundle,
  type UpdateMessageBundleDto,
} from '../../../services/hub.service';
import { hubClient } from '../../../services/hubClient';
import { BUNDLES_RETIRED_NOTE, bundlesRetired } from '../../../lib/blockLinksView';
import { AiConfigModal, AiConfigTabShell } from '../index';

/** Hub props / state may be numeric ids; never call `.trim()` on unknown. */
function trimStr(v: unknown): string {
  return String(v ?? '').trim();
}

function linesToArray(s: string): string[] {
  return s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

const DEFAULT_PAYLOAD = '[{"text":"Hello"}]';

export type MessageBundlesTabProps = {
  /** Hub bot id (string or number from API): when set, list and new bundles are scoped to this bot. */
  botId?: string | number;
  /** Defaults to `chatbot` when omitted. When passed with `botId`, system filter is fixed. */
  system?: string | number;
};

export function MessageBundlesTab({ botId: lockedBotId, system: lockedSystem }: MessageBundlesTabProps = {}) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<MessageBundle[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);

  const [system, setSystem] = useState(() => trimStr(lockedSystem ?? 'chatbot') || 'chatbot');
  const [botId, setBotId] = useState(() => (lockedBotId != null ? String(lockedBotId) : ''));

  useEffect(() => {
    if (lockedSystem != null) setSystem(trimStr(lockedSystem) || 'chatbot');
  }, [lockedSystem]);
  useEffect(() => {
    if (lockedBotId != null) setBotId(String(lockedBotId));
  }, [lockedBotId]);
  // A bot on block_links=on: bundles are read-only (the hub refuses writes with 409 bundles_retired).
  const [retired, setRetired] = useState(false);
  useEffect(() => {
    setRetired(false);
    if (lockedBotId == null || (lockedSystem !== 'chatbot' && lockedSystem !== 'chatbot_v2')) return;
    let live = true;
    hubClient
      .getBotRouting({ system: lockedSystem, botId: String(lockedBotId) })
      .then((r) => live && setRetired(bundlesRetired(r.flow)))
      .catch(() => undefined); // unreadable = editable; the hub still refuses
    return () => {
      live = false;
    };
  }, [lockedSystem, lockedBotId]);
  const [channel, setChannel] = useState('');
  const [keyFilter, setKeyFilter] = useState('');

  const [detail, setDetail] = useState<MessageBundle | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MessageBundle | null>(null);

  const [formSystem, setFormSystem] = useState('chatbot');
  const [formBotId, setFormBotId] = useState('');
  const [formKey, setFormKey] = useState('');
  const [formName, setFormName] = useState('');
  const [formPurpose, setFormPurpose] = useState('');
  const [formChannel, setFormChannel] = useState('messenger');
  const [formPayload, setFormPayload] = useState(DEFAULT_PAYLOAD);
  const [formSlots, setFormSlots] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formExamples, setFormExamples] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    setOperationError(null);
    try {
      const token = getToken();
      const res = await hubService.listMessageBundles(
        {
          system: trimStr(lockedSystem ?? system) || undefined,
          botId: trimStr(lockedBotId ?? botId) || undefined,
          channel: channel.trim() || undefined,
          key: keyFilter.trim() || undefined,
          limit: 200,
          offset: 0,
        },
        token,
      );
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken, lockedSystem, system, lockedBotId, botId, channel, keyFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  function resetCreateForm() {
    setFormSystem(trimStr(lockedSystem ?? 'chatbot') || 'chatbot');
    setFormBotId(lockedBotId != null ? String(lockedBotId) : '');
    setFormKey('');
    setFormName('');
    setFormPurpose('');
    setFormChannel('messenger');
    setFormPayload(DEFAULT_PAYLOAD);
    setFormSlots('');
    setFormTags('');
    setFormExamples('');
  }

  function openEdit(b: MessageBundle) {
    setFormError(null);
    setEditing(b);
    setFormSystem(trimStr(b.system) || 'chatbot');
    setFormBotId(String(b.botId ?? ''));
    setFormKey(b.key);
    setFormName(b.name);
    setFormPurpose(b.purpose ?? '');
    setFormChannel(b.channel || 'messenger');
    setFormPayload(JSON.stringify(b.payload_json ?? [], null, 2));
    setFormSlots((b.required_slots ?? []).join('\n'));
    setFormTags((b.intent_tags ?? []).join('\n'));
    setFormExamples((b.trigger_examples ?? []).join('\n'));
  }

  async function handleCreate() {
    setFormError(null);
    let payload: unknown[];
    try {
      const parsed = JSON.parse(formPayload || '[]');
      if (!Array.isArray(parsed)) throw new Error('payload_json must be a JSON array');
      payload = parsed;
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Invalid payload JSON');
      return;
    }
    if (!formBotId.trim() || !formKey.trim() || !formName.trim()) {
      setFormError('botId, key, and name are required');
      return;
    }
    const dto: CreateMessageBundleDto = {
      system: formSystem.trim() || 'chatbot',
      botId: formBotId.trim(),
      key: formKey.trim(),
      name: formName.trim(),
      purpose: formPurpose.trim() || undefined,
      channel: formChannel.trim() || 'messenger',
      payload_json: payload,
      required_slots: linesToArray(formSlots),
      intent_tags: linesToArray(formTags),
      trigger_examples: linesToArray(formExamples),
    };
    try {
      const token = getToken();
      await hubService.createMessageBundle(dto, token);
      setCreating(false);
      resetCreateForm();
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleUpdate() {
    if (!editing) return;
    setFormError(null);
    const dto: UpdateMessageBundleDto = {
      required_slots: linesToArray(formSlots),
      intent_tags: linesToArray(formTags),
      trigger_examples: linesToArray(formExamples),
    };
    try {
      const token = getToken();
      await hubService.updateMessageBundle(editing.id, dto, token);
      setEditing(null);
      resetCreateForm();
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this message bundle?')) return;
    try {
      const token = getToken();
      await hubService.deleteMessageBundle(id, token);
      await load();
      if (detail?.id === id) setDetail(null);
    } catch (e) {
      setOperationError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <AiConfigTabShell loading={loading} error={listError}>
      <div className="space-y-6">
        {operationError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2 flex justify-between gap-2">
            <span>{operationError}</span>
            <button type="button" className="text-gray-600 hover:underline shrink-0" onClick={() => setOperationError(null)}>
              Dismiss
            </button>
          </div>
        )}
        <p className="text-sm text-gray-600">
          Hub <code className="text-xs bg-gray-100 px-1 rounded">GET /api/message-bundles</code>.
          {lockedBotId != null ? (
            <> Bundles for this bot only.</>
          ) : (
            <> Filter by system / bot / channel / key; inspect payload and tags; create or edit bundles.</>
          )}
        </p>

        <div className="flex flex-wrap gap-2 items-end bg-white p-4 rounded-lg border border-gray-200">
          {lockedSystem != null ? (
            <p className="text-xs text-gray-600">
              system <span className="font-mono text-sm">{lockedSystem}</span>
            </p>
          ) : (
            <label className="text-xs text-gray-600">
              system
              <input
                value={system}
                onChange={(e) => setSystem(e.target.value)}
                className="block mt-1 w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>
          )}
          {lockedBotId != null ? (
            <p className="text-xs text-gray-600">
              botId <span className="font-mono text-sm break-all">{lockedBotId}</span>
            </p>
          ) : (
            <label className="text-xs text-gray-600">
              botId
              <input
                value={botId}
                onChange={(e) => setBotId(e.target.value)}
                placeholder="optional"
                className="block mt-1 w-36 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>
          )}
          <label className="text-xs text-gray-600">
            channel
            <input
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              placeholder="optional"
              className="block mt-1 w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-gray-600">
            key
            <input
              value={keyFilter}
              onChange={(e) => setKeyFilter(e.target.value)}
              placeholder="optional"
              className="block mt-1 w-36 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => void load()}
            className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded"
          >
            Refresh
          </button>
          <span className="text-xs text-gray-500 ml-auto">{total} total</span>
        </div>

        {retired && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2">{BUNDLES_RETIRED_NOTE}</p>
        )}

        {retired ? null : !creating && !editing ? (
          <button
            type="button"
            onClick={() => {
              resetCreateForm();
              setFormError(null);
              setCreating(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            New bundle
          </button>
        ) : (
          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <h3 className="font-medium text-gray-900">{editing ? 'Edit bundle' : 'Create bundle'}</h3>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs text-gray-600">
                system
                <input
                  placeholder="chatbot"
                  value={formSystem}
                  onChange={(e) => setFormSystem(e.target.value)}
                  disabled={!!editing || lockedSystem != null}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </label>
              <label className="text-xs text-gray-600">
                botId
                <input
                  placeholder="bot uuid"
                  value={formBotId}
                  onChange={(e) => setFormBotId(e.target.value)}
                  disabled={!!editing || lockedBotId != null}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </label>
              <label className="text-xs text-gray-600">
                key
                <input
                  placeholder="unique key per bot"
                  value={formKey}
                  onChange={(e) => setFormKey(e.target.value)}
                  disabled={!!editing}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </label>
              <label className="text-xs text-gray-600">
                name
                <input
                  placeholder="display name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={!!editing}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </label>
              <label className="text-xs text-gray-600">
                channel
                <input
                  placeholder="messenger"
                  value={formChannel}
                  onChange={(e) => setFormChannel(e.target.value)}
                  disabled={!!editing}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </label>
            </div>
            <label className="block text-xs text-gray-600">
              purpose (optional)
              <input
                placeholder="What this bundle is for"
                value={formPurpose}
                onChange={(e) => setFormPurpose(e.target.value)}
                disabled={!!editing}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
              />
            </label>
            <label className="block text-xs text-gray-600">payload_json (JSON array)</label>
            <textarea
              value={formPayload}
              onChange={(e) => setFormPayload(e.target.value)}
              disabled={!!editing}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono disabled:bg-gray-100"
              rows={8}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-600">required_slots (one per line)</label>
                <textarea
                  value={formSlots}
                  onChange={(e) => setFormSlots(e.target.value)}
                  className="w-full mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">intent_tags (one per line)</label>
                <textarea
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  className="w-full mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">trigger_examples (one per line)</label>
                <textarea
                  value={formExamples}
                  onChange={(e) => setFormExamples(e.target.value)}
                  className="w-full mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => (editing ? void handleUpdate() : void handleCreate())}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                  setFormError(null);
                  resetCreateForm();
                }}
                className="px-3 py-1.5 text-sm bg-gray-200 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Key</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Bot</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">System</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Ch</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Payload</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Updated</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((b) => (
                <tr key={b.id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{b.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.key}</td>
                  <td className="px-3 py-2">{b.botId}</td>
                  <td className="px-3 py-2">{b.system}</td>
                  <td className="px-3 py-2">{b.channel}</td>
                  <td className="px-3 py-2 text-gray-600">{(b.payload_json ?? []).length} msgs</td>
                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {b.updatedAt ? new Date(b.updatedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => setDetail(b)}>
                      View
                    </button>
                    {!retired && (
                      <>
                        <button type="button" className="text-blue-600 hover:underline" onClick={() => openEdit(b)}>
                          Edit
                        </button>
                        <button type="button" className="text-red-600 hover:underline" onClick={() => void handleDelete(b.id)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && !loading && (
            <p className="p-4 text-sm text-gray-500">No bundles match filters.</p>
          )}
        </div>
      </div>

      {detail && (
        <AiConfigModal title={`Bundle: ${detail.name}`} onClose={() => setDetail(null)} maxWidth="lg">
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-gray-500">id</span> <span className="font-mono text-xs">{detail.id}</span>
            </p>
            <p>
              <span className="text-gray-500">key</span> <span className="font-mono">{detail.key}</span>
            </p>
            <p>
              <span className="text-gray-500">bot / system</span> {detail.botId} / {detail.system}
            </p>
            {detail.purpose && (
              <p>
                <span className="text-gray-500">purpose</span> {detail.purpose}
              </p>
            )}
            <p className="text-gray-500">required_slots</p>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-24">
              {JSON.stringify(detail.required_slots ?? [], null, 2)}
            </pre>
            <p className="text-gray-500">intent_tags</p>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-24">
              {JSON.stringify(detail.intent_tags ?? [], null, 2)}
            </pre>
            <p className="text-gray-500">trigger_examples</p>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-24">
              {JSON.stringify(detail.trigger_examples ?? [], null, 2)}
            </pre>
            <p className="text-gray-500">payload_json</p>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-64">
              {JSON.stringify(detail.payload_json ?? [], null, 2)}
            </pre>
            <button type="button" className="text-sm text-gray-600 hover:underline" onClick={() => setDetail(null)}>
              Close
            </button>
          </div>
        </AiConfigModal>
      )}
    </AiConfigTabShell>
  );
}
